import * as CL from '@design-system/component-library';
import { useState } from 'react';
import { paths } from '../../common/constants/pathVariables.js';
import { DeviceVariantProductTypeCoCo } from '../../common/enums.js';
import { useDeviceListControlsCoCo } from '../../common/hooks/useDeviceListControlsCoCo.js';
import { alvZeroMsg, eurosPerMonthMsg, noResultsFoundMsg, t } from '../../common/i18n/index.js';
import { getFirstDeviceSelectionCategory } from '../../common/utils/deviceSelectionCategoryUtilsCoCo.js';
import {
  getDeviceSelectionDevices,
  getDeviceSelectionPrice,
  toDeviceSelectionVariantDevices,
} from '../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import { centsToEuros } from '../../common/utils/priceUtils.js';
import { getLocalizedString } from '../../common/utils/productOfferingsUtils.js';
import { toKauppaSlug } from '../DeviceDetailsCoCo/deviceDetailsUtilsCoCo.js';
import { DeviceSelectionCategorySelectorCoCo } from '../DeviceSelectionFormCoCo/components/DeviceSelectionCategoriesCoCo.js';
import { DeviceSelectionDevicesToolbarCoCo } from '../DeviceSelectionFormCoCo/components/DeviceSelectionDevicesToolbarCoCo.js';
import type { DeviceSelectionProductTypeCoCo } from '../../common/enums.js';
import type {
  DeviceSelectionDeviceCoCo,
  DeviceVariantGroupsByTypeCoCo,
} from '../../common/utils/deviceSelectionDeviceUtilsCoCo.js';
import type { DeviceVariantGroup } from '../../generated/api/deviceVariantGroup.js';

import './EoeDeviceStoreProductsCoCo.scss';

interface EoeDeviceStoreProductsCoCoProps {
  productType: DeviceSelectionProductTypeCoCo;
  variantGroups: DeviceVariantGroupsByTypeCoCo;
  allowedVariantGroupIds: string[];
  onSelectDevice?: (path: string) => void;
}

const EmployeeDeviceCard = ({
  device,
  group,
  onSelectDevice,
}: {
  device: DeviceSelectionDeviceCoCo;
  group?: DeviceVariantGroup;
  onSelectDevice?: (path: string) => void;
}) => {
  const offer = group?.productOfferings[0];
  const productPath = offer
    ? `${paths.EMPLOYEE_DEVICE_STORE_PHONES}/${toKauppaSlug(getLocalizedString(offer.name))}?${new URLSearchParams({ offerId: offer.id })}`
    : undefined;

  return (
    <CL.ProductCard
      productName={device.name}
      productURL={productPath}
      onProductClick={(_id, event) => {
        if (productPath && onSelectDevice) {
          event.preventDefault();
          onSelectDevice(productPath);
        }
      }}
      productImage={
        group?.variantImageUrl ? (
          <CL.Picture src={group.variantImageUrl} i18n_picture_altText={device.name} />
        ) : (
          <div className="of-employee-device-store-products-coco__missing-image">
            <CL.Icon icon="devices" size="xl" aria-hidden="true" />
          </div>
        )
      }
      productPrice={
        <CL.Price
          currentPrice={String(centsToEuros(getDeviceSelectionPrice(device)))}
          currentPriceUnit={device.oneTimePrice === undefined ? t.H37G(eurosPerMonthMsg) : '€'}
          additionalInfo={t.S8TX(alvZeroMsg)}
        />
      }
    />
  );
};

const DeviceCards = ({
  devices,
  variantGroups,
  onSelectDevice,
}: {
  devices: DeviceSelectionDeviceCoCo[];
  variantGroups: DeviceVariantGroupsByTypeCoCo;
  onSelectDevice?: (path: string) => void;
}) => {
  const { filters, search, sort, setFilters, setSearch, setSort, visibleDevices } = useDeviceListControlsCoCo(devices);
  const groupsByType = {
    [DeviceVariantProductTypeCoCo.EPP]: new Map(
      variantGroups[DeviceVariantProductTypeCoCo.EPP].map(group => [group.variantGroupId, group])
    ),
    [DeviceVariantProductTypeCoCo.Device]: new Map(
      variantGroups[DeviceVariantProductTypeCoCo.Device].map(group => [group.variantGroupId, group])
    ),
  };

  return (
    <>
      <DeviceSelectionDevicesToolbarCoCo
        filters={filters}
        search={search}
        sort={sort}
        onFiltersChange={setFilters}
        onSearchChange={setSearch}
        onSortChange={setSort}
      />
      {visibleDevices.length > 0 ? (
        <CL.ProductGridV2>
          {visibleDevices.map(device => {
            const isOneTime = device.oneTimePrice !== undefined;
            const group: DeviceVariantGroup | undefined = groupsByType[
              isOneTime ? DeviceVariantProductTypeCoCo.Device : DeviceVariantProductTypeCoCo.EPP
            ].get(device.id);
            return <EmployeeDeviceCard key={device.id} device={device} group={group} onSelectDevice={onSelectDevice} />;
          })}
        </CL.ProductGridV2>
      ) : (
        <output>{devices.length === 0 ? t.TR6I('No devices in this category.') : t.OQBT(noResultsFoundMsg)}</output>
      )}
    </>
  );
};

export const EoeDeviceStoreProductsCoCo = ({
  productType,
  variantGroups,
  allowedVariantGroupIds,
  onSelectDevice,
}: EoeDeviceStoreProductsCoCoProps) => {
  const [category, setCategory] = useState(() => getFirstDeviceSelectionCategory(productType));
  const allowedIds = new Set(allowedVariantGroupIds);
  const devices = toDeviceSelectionVariantDevices(variantGroups, productType).filter(device =>
    allowedIds.has(device.id)
  );

  return (
    <section className="of-employee-device-store-products-coco">
      <DeviceSelectionCategorySelectorCoCo
        name="employee-device-category"
        productType={productType}
        value={category}
        onChange={setCategory}
      />
      <DeviceCards
        key={category}
        devices={getDeviceSelectionDevices(category, devices)}
        variantGroups={variantGroups}
        onSelectDevice={onSelectDevice}
      />
    </section>
  );
};
